import React, { useEffect, useRef } from 'react';

// SkullVideo — replaces the 3D R3F SkullScene at the climax of the deck.
//
// The user provided a pre-rendered holographic skull MP4 (with a bullet
// pass-through baked in). We composite it on top of the dark deck background
// using CSS `mix-blend-mode: screen`, which:
//   • drops the video's near-black pixels to transparent
//   • lets the bright cyan/white/red highlights "punch through" cleanly
//   • gives the same JARVIS-style ghostly hologram feel as the previous 3D
//     scene, without the 71MB GLB / WebGL cost.
//
// Scroll behavior:
//   • The video appears when the deck stage flips to 'skull' (p ≥ 0.82).
//   • We loop the video so it always reads as a "live projection".
//   • We adjust playback rate slightly with scroll: faster motion as the
//     viewer rushes into the climax, slower while reading.
//
// This component intentionally accepts the same `progressRef` /
// `segmentTargets` signature as the old SkullScene so HologramStage can use
// it as a drop-in replacement.
export default function SkullVideo({ progressRef, active }) {
  const videoRef = useRef(null);

  // Ensure autoplay works across browsers (must be muted + playsInline).
  // We also force a .load() + tick the muted/play attempts on first mount so
  // the video element actually pulls frames even while its wrapper is at
  // opacity 0 (some browsers skip loading hidden media otherwise).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    try { v.load(); } catch (_) {}
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    tryPlay();
    // Retry once the metadata is in
    const onMeta = () => tryPlay();
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('canplay', onMeta);
    // Unlock on first user gesture (Safari/iOS hard requirement)
    const unlock = () => tryPlay();
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
    document.addEventListener('scroll', unlock, { once: true, passive: true });
    return () => {
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('canplay', onMeta);
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('scroll', unlock);
    };
  }, []);

  // When the stage flips to "skull", FORCE the video to load + play. This
  // covers the case where Chrome's hidden-media optimisation skipped the
  // initial preload while the wrapper was at opacity 0.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active) {
      try { v.load(); } catch (_) {}
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }, [active]);

  // Scroll-driven playback rate so the climax feels reactive without ever
  // pausing the video entirely.
  useEffect(() => {
    let raf;
    const tick = () => {
      const v = videoRef.current;
      if (v && progressRef && progressRef.current != null) {
        const p = progressRef.current; // 0..1
        // Local "climax depth" — 0 when the skull first appears (p≈0.82),
        // 1 at the very end of the deck. Faster motion as the user descends.
        const localP = Math.max(0, Math.min(1, (p - 0.82) / 0.16));
        const rate = 0.85 + localP * 0.65; // 0.85 → 1.50
        if (Math.abs(v.playbackRate - rate) > 0.02) v.playbackRate = rate;
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => raf && cancelAnimationFrame(raf);
  }, [progressRef]);

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 flex items-center justify-center"
      style={{ pointerEvents: 'none' }}
    >
      <video
        ref={videoRef}
        data-testid="skull-hologram-video"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        style={{
          // Anchor the skull video toward the right half of the viewport so it
          // mirrors the old 3D scene's framing (right-leaning, then centers as
          // the deck closes).
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 'min(90vw, 1200px)',
          height: 'min(90vh, 800px)',
          transform: 'translate(-50%, -50%)',
          objectFit: 'contain',
          // Screen blending turns the video's near-black pixels invisible and
          // lets the bright holographic content composite over the deck.
          mixBlendMode: 'screen',
          // Crush the blacks: the source video's background is dark teal, not
          // pure #000. Boosting contrast and slightly lowering brightness
          // drops those dark pixels close to zero so 'screen' fully hides them
          // (no visible rectangle), while the bright cyan / red highlights
          // stay vibrant for the projection effect.
          filter: 'contrast(1.55) brightness(0.95) saturate(1.2)',
          // Soft radial mask to feather any residual rectangular edge.
          WebkitMaskImage:
            'radial-gradient(ellipse 60% 70% at 50% 50%, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)',
          maskImage:
            'radial-gradient(ellipse 60% 70% at 50% 50%, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)',
        }}
      >
        {/* WebM (VP9) listed first — guaranteed to play in open-source
            Chromium / Firefox builds. Browsers that prefer / only support
            H.264 will fall through to the MP4. */}
        <source src="/assets/video/skull-holo.webm" type="video/webm" />
        <source src="/assets/video/skull-holo.mp4" type="video/mp4" />
      </video>    </div>
  );
}
